import React, { useContext, useEffect, useMemo, useCallback } from 'react';
import { policyTypes } from '@ourbranch/policy-types';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { isDraftablePolicyType } from 'core/helpers/policy-drafting';
import { isPast, isToday, subDays, startOfDay } from 'date-fns';
import { toDate } from 'date-fns-tz';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';

import { getPolicyStatus, PolicyStatus } from 'core/helpers/policy-status';
import { removeSpecificQueryParam } from 'core/helpers/url-utils';
import { track } from 'core/helpers/analytics';
import { AuthContext } from 'core/components/auth';
import { ValidationErrorsModal, useValidationErrorsModal } from 'core/components/validation-errors-modal';
import { useStore } from 'core/store';
import { PolicyModalTypes } from 'core/store/account-store/policies-store/policy-store/policy-store';
import { STAFF_AUTO_MAX_BACKDATE_DAYS } from '../dates';
import Footer from '../footer';
import { getNewChangeDateConflict } from './helpers';
import { SegmentFormLeft, SegmentFormRight } from '../segment-form';
import { HomePolicy } from './home-policy';
import { CondoPolicy } from './condo-policy';
import { AutoPolicy } from './auto-policy';
import { RentersPolicy } from './renters-policy';
import { FarmPolicy } from './farm-policy';
import PreviewFooter from '../footer/preview-footer';

import useStyles from './details.styles';

const DetailsForm = observer(function DetailsForm({
  loadingPreview,
  handleRadioChange,
  state,
  handleStartDate,
  repEmail
}) {
  const { touched, handleSubmit, errors, validateForm, setErrors, setTouched } = useFormikContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();
  const history = useHistory();

  const { account } = useStore();
  const {
    policies: {
      policy: {
        segment,
        policy,
        preview,
        changed: changedStatus,
        setChanged,
        loading,
        setActivePolicyModal,
        setScrollTo
      }
    }
  } = account;

  const session = useContext(AuthContext);
  const classes = useStyles();
  const changed = useMemo(() => session?.canEditPolicies && Object.keys(touched).length > 0, [touched, session]);
  const editingFirstSegment =
    policy.segments[0].segmentId === segment.segmentId && state.get('changeType') === 'edit_change';

  const policyStatus = loading ? null : getPolicyStatus(policy);
  const cancelled = policyStatus === PolicyStatus.InCancellation || policyStatus === PolicyStatus.Cancelled;

  // Reactively flag a "Create New Change" effective date that collides with an existing
  // segment, so the date picker can show inline guidance up front (#31068). The agent can
  // still preview; the backend rejection is then caught and surfaced via the neutral
  // WarningModal. Edit path keeps its own handling.
  const startDateConflict =
    state.get('changeType') === 'new_change' && state.get('startDate')
      ? getNewChangeDateConflict({ policy, startDate: state.get('startDate') })
      : null;

  const footerOptions = () => {
    const options = [{ value: 'new_change', label: 'Create New Change' }];
    const segmentDate = toDate(segment.startDate);
    // Past auto segments within the 30-day backdate window remain editable for non-canBackDate users —
    // disallowed change types are caught by the submission-time validator in details.jsx.
    const isAutoWithinBackdateWindow =
      policy.policyType?.includes(policyTypes.Auto) &&
      segmentDate >= startOfDay(subDays(new Date(), STAFF_AUTO_MAX_BACKDATE_DAYS));
    if (isPast(segmentDate) && !session?.canBackDate && !isToday(segmentDate) && !isAutoWithinBackdateWindow) {
      return options;
    }
    options.push({ value: 'edit_change', label: 'Edit Selected Change' });
    return options;
  };

  useEffect(() => {
    if (changed && !changedStatus) {
      setChanged(true);
    } else if (!changed) {
      setChanged(false);
    }
  }, [changed, changedStatus, setChanged]);

  useEffect(() => {
    if (Object.keys(errors).length) {
      // logging validation errors on details pane for better visbility of production issues
      console.log({ errors });
    }
  }, [errors]);

  const onSubmit = useCallback(async () => {
    const errors = await validateForm();

    if (Object.keys(errors).length) {
      setErrors(errors);
      setTouched(errors);
      setShowValidationErrorsModal(true);
    } else {
      handleSubmit();
    }
  }, [handleSubmit, setShowValidationErrorsModal, validateForm, setErrors, setTouched]);

  useEffect(() => {
    if (Object.keys(touched).length && preview?.id && !isDraftablePolicyType(policy.policyType)) {
      removeSpecificQueryParam(history, 'previewId');
    }
  }, [touched, preview?.id]);

  // Validation Errors modal "go to section" (#30230). Policy details is a single page (no tabs),
  // so just scroll to the anchor (setScrollTo drives useScroll → scroll + flash) and close the
  // modal. `target` is the resolved nav target from the modal ({ anchor }).
  const handleGoToSection = ({ anchor }) => {
    track('Staff Validation Error Go To Section Clicked', { section: anchor, flow: 'policy' });
    setScrollTo(anchor);
    setShowValidationErrorsModal(false);
  };

  return (
    <div className={changed ? classes.changed : null}>
      {/* need policy to know if we are on a bix policy in a nested component */}

      {policy.policyType === policyTypes.Auto && <AutoPolicy />}
      {policy.policyType === policyTypes.Home && <HomePolicy repEmail={repEmail} />}
      {policy.policyType === policyTypes.Condo && <CondoPolicy />}
      {policy.policyType === policyTypes.Renters && <RentersPolicy />}
      {isFarmBasedPolicyType(policy.policyType) && <FarmPolicy />}

      {changed && !session.viewOnly && !cancelled && !preview?.id && policy.canEndorse !== false && (
        <Footer
          loading={loadingPreview}
          leftForm={
            <SegmentFormLeft
              onRadioChange={handleRadioChange}
              options={footerOptions()}
              value={state.get('changeType')}
            />
          }
          form={
            <SegmentFormRight
              editingFirstSegment={editingFirstSegment}
              isEditingSegment={state.get('changeType') === 'edit_change'}
              onChange={handleStartDate}
              startDate={state.get('startDate')}
              minDate={state.get('minDate')}
              maxDate={state.get('maxDate')}
              error={startDateConflict}
              policy={policy}
            />
          }
          onClick={onSubmit}
          policy={policy}
        />
      )}
      {preview?.id && !cancelled && !session.viewOnly && (
        <PreviewFooter
          onSubmit={async () => {
            if (isDraftablePolicyType(policy.policyType) && changed) {
              await onSubmit();
              return;
            }

            setActivePolicyModal(PolicyModalTypes.PREVIEW);
          }}
          policyPreview={preview}
        />
      )}
      <ValidationErrorsModal
        open={showValidationErrorsModal}
        onClose={() => setShowValidationErrorsModal(false)}
        onGoToSection={handleGoToSection}
      />
    </div>
  );
});

DetailsForm.propTypes = {
  loadingPreview: PropTypes.bool.isRequired,
  handleRadioChange: PropTypes.func.isRequired,
  state: PropTypes.object.isRequired,
  handleStartDate: PropTypes.func.isRequired,
  repEmail: PropTypes.string.isRequired
};

export default DetailsForm;
